// frontend/src/motion/useInViewGate.js
//
// "Is this thing worth running right now?" - the viewport half of that answer.
//
// Canvases, timers and count-ups below the fold must not burn a frame budget
// nobody is looking at. This is the shared gate for them: an IntersectionObserver
// that reports whether the observed element is on screen, with three deliberate
// behaviours the ad-hoc `useInView` call sites in this repo do not all get right:
//
//   1. `window.__PRERENDER__` forces `true` and observes nothing. Puppeteer
//      renders the prerender snapshot at 800x600 with no scrolling, so every
//      below-the-fold observer would otherwise never fire and the snapshot would
//      bake the not-yet-revealed state into the HTML we serve to crawlers.
//   2. No IntersectionObserver at all (jsdom, very old Safari) fails OPEN. A
//      missing capability must not hide content.
//   3. A callback ref, not a plain ref object, so an element that mounts later
//      (a lazily-mounted canvas) is still picked up.
//
// ONE OBSERVER PER OPTION SET, not one per hook (perf batch, 2026-09-04). The
// page mounts ~60 of these - every reveal, every section shell, every gated
// artefact - and each used to construct its own IntersectionObserver. Observers
// with identical `rootMargin` + `threshold` now share one instance from a
// module-level pool, keyed on those options, with the callbacks routed to the
// right element through a WeakMap. Semantics are unchanged: a hook still sees
// only its own element, `once` still stops ITS observation, and the pool entry
// is torn down when its last subscriber leaves.
//
// It answers ONLY the viewport question. Motion preference is a separate axis -
// combine with `useMotion()` at the call site, so the two reasons a loop is idle
// stay individually inspectable.

import { useCallback, useEffect, useState } from "react";

/** @returns {boolean} true while the prerenderer is driving the page. */
export function isPrerendering() {
  return typeof window !== "undefined" && window.__PRERENDER__ === true;
}

const hasObserver = () => typeof IntersectionObserver !== "undefined";

// ------------------------------------------------------------------ the pool

/**
 * @typedef {object} PoolEntry
 * @property {IntersectionObserver} observer
 * @property {WeakMap<Element, Set<(visible: boolean) => void>>} listeners
 * @property {number} size how many subscriptions the entry currently serves.
 */

/** @type {Map<string, PoolEntry>} */
const pool = new Map();

/**
 * Observe `node` with a shared observer for these options.
 *
 * @param {Element} node
 * @param {string} rootMargin
 * @param {string} thresholdKey JSON of the threshold (a stable key).
 * @param {(visible: boolean) => void} onChange
 * @returns {() => void} unsubscribe; idempotent.
 */
function observeShared(node, rootMargin, thresholdKey, onChange) {
  const key = `${rootMargin}|${thresholdKey}`;
  let entry = pool.get(key);
  if (!entry) {
    const listeners = new WeakMap();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const item of entries) {
          const set = listeners.get(item.target);
          if (!set) continue;
          // A listener may unsubscribe while being notified (`once`); Set
          // iteration tolerates deletion mid-walk.
          set.forEach((listener) => listener(item.isIntersecting));
        }
      },
      { rootMargin, threshold: JSON.parse(thresholdKey) }
    );
    entry = { observer, listeners, size: 0 };
    pool.set(key, entry);
  }

  let set = entry.listeners.get(node);
  const fresh = !set;
  if (fresh) {
    set = new Set();
    entry.listeners.set(node, set);
  }
  // Register BEFORE observing: a stub (and, in principle, a browser) may
  // deliver the first entry synchronously from observe().
  set.add(onChange);
  entry.size += 1;
  if (fresh) entry.observer.observe(node);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const current = pool.get(key);
    if (current !== entry) return;
    const listenersForNode = entry.listeners.get(node);
    if (listenersForNode) {
      listenersForNode.delete(onChange);
      if (listenersForNode.size === 0) {
        entry.listeners.delete(node);
        if (typeof entry.observer.unobserve === "function") entry.observer.unobserve(node);
      }
    }
    entry.size -= 1;
    if (entry.size <= 0) {
      entry.observer.disconnect();
      pool.delete(key);
    }
  };
}

/** Test-only: disconnect every pooled observer and forget them. */
export function __resetInViewPoolForTests() {
  pool.forEach((entry) => {
    try {
      entry.observer.disconnect();
    } catch {
      /* a stub without disconnect */
    }
  });
  pool.clear();
}

/** @returns {number} how many distinct observers the pool currently holds. */
export function getPooledObserverCount() {
  return pool.size;
}

// ------------------------------------------------------------------ the hook

/**
 * @param {object} [options]
 * @param {string} [options.rootMargin] IntersectionObserver rootMargin.
 * @param {number|number[]} [options.threshold] IntersectionObserver threshold.
 * @param {boolean} [options.once] stop observing after the first intersection.
 * @param {boolean} [options.initialInView] the value reported BEFORE the
 *   observer's first callback (default false). `true` is for state that must
 *   match the prerendered snapshot on hydration - the observer corrects it on
 *   its first delivery either way.
 * @returns {[ (node: Element|null) => void, boolean ]} `[ref, inView]`.
 */
export default function useInViewGate(options = {}) {
  const { rootMargin = "0px", threshold = 0, once = false, initialInView = false } = options;

  // Forced open when we cannot - or must not - observe. Computed lazily so the
  // very first render is already correct.
  const [forcedOpen] = useState(() => isPrerendering() || !hasObserver());
  const [inView, setInView] = useState(forcedOpen || initialInView);
  const [node, setNode] = useState(null);

  const ref = useCallback((next) => setNode(next || null), []);

  // threshold may legitimately be an array; serialise it so an inline literal
  // does not re-create the subscription on every render. Normalised to an
  // array first, so `0` and `[0]` (the same observer to the browser) share
  // one pool entry rather than two.
  const thresholdKey = JSON.stringify([].concat(threshold).map(Number));

  useEffect(() => {
    if (forcedOpen || !node) return undefined;

    let release = null;
    let finished = false;
    const onChange = (isVisible) => {
      if (finished) return;
      setInView(isVisible);
      if (isVisible && once) {
        finished = true;
        if (release) release();
      }
    };
    release = observeShared(node, rootMargin, thresholdKey, onChange);
    if (finished) release();
    return release;
  }, [node, forcedOpen, rootMargin, thresholdKey, once]);

  return [ref, inView];
}
